import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../requestOptions';

export interface RenderedValuesGridColumn {
  assignment_id: number;
  attribute_name: string;
  attribute_path: string;
  mapping_type: string;
  attribute_value?: string | null;
  value_type: string;
  min_occurs: number;
  max_occurs: number;
}

export interface RenderedValuesGridCell {
  original_value: string | null;
  final_value: string | null;
  original_value_source: string;
  final_value_source: string;
  validation_status: string;
  validation_errors?: string[] | null;
}

export interface RenderedValuesGridRow {
  product_id: number;
  sku: string;
  cells: RenderedValuesGridCell[];
}

export interface RenderedValuesGridData {
  columns: RenderedValuesGridColumn[];
  rows: RenderedValuesGridRow[];
  total: number;
  page: number;
  per_page: number;
}

type GridApiEnvelope = {
  data?: { columns?: RenderedValuesGridColumn[]; rows?: RenderedValuesGridRow[] };
  meta?: { total?: number; current_page?: number; per_page?: number; last_page?: number };
  columns?: RenderedValuesGridColumn[];
  rows?: RenderedValuesGridRow[];
  total?: number;
  page?: number;
  per_page?: number;
};

export async function getRenderedValuesGrid(
  scope: RequestScope,
  args: {
    subscriptionId: number;
    postingTemplateId: number;
    page?: number;
    perPage?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    errorsOnly?: boolean;
    inStockOnly?: boolean;
  },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<RenderedValuesGridData>> {
  try {
    if (!args.subscriptionId || args.subscriptionId < 1) {
      throw new Error('Valid subscriptionId is required');
    }
    if (!args.postingTemplateId || args.postingTemplateId < 1) {
      throw new Error('Valid postingTemplateId is required');
    }

    const params: Record<string, string | number | boolean> = {
      posting_template_id: args.postingTemplateId,
    };

    if (args.page !== undefined) params.page = args.page;
    if (args.perPage !== undefined) params.per_page = args.perPage;
    if (args.sortBy) params.sort_by = args.sortBy;
    if (args.sortDir) params.sort_dir = args.sortDir;
    if (args.errorsOnly) params.errors_only = true;
    if (args.inStockOnly) params.in_stock_only = true;

    const res = await scopedChannelApi(scope).get<GridApiEnvelope>('atts/rendered_values/grid', {
      ...options,
      headers: {
        'x-astur-subscription-id': args.subscriptionId.toString(),
      },
      params,
    });

    const envelope = res.data;
    const nested = envelope?.data;
    const meta = envelope?.meta;
    const data: RenderedValuesGridData = nested
      ? {
          columns: nested.columns || [],
          rows: nested.rows || [],
          total: meta?.total ?? 0,
          page: meta?.current_page ?? 1,
          per_page: meta?.per_page ?? 50,
        }
      : {
          columns: envelope?.columns || [],
          rows: envelope?.rows || [],
          total: envelope?.total || 0,
          page: envelope?.page || 1,
          per_page: envelope?.per_page || 50,
        };

    return { data, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}
